import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { ACCENT_COLORS, TIMEZONES } from "@/lib/constants";
import { Loader2, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [form, setForm] = useState({
    bakery_name: "", owner_name: user?.name || "", location: "", timezone: "America/New_York",
    description: "", slug: "", accent_color: "#BD806A", pickup_instructions: "",
    payment_instructions: "Pay with Venmo after placing your order, or cash at pickup.",
    contact_email: user?.email || "",
  });

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "bakery_name" && !slugEdited) next.slug = slugify(v);
    return next;
  });

  const steps = [
    { title: "Name your bakery", fields: ["bakery_name", "owner_name", "location"] },
    { title: "Tell customers your story", fields: ["description", "slug", "accent_color"] },
    { title: "Pickup & payment details", fields: ["timezone", "pickup_instructions", "payment_instructions", "contact_email"] },
  ];

  const canNext = () => {
    if (step === 0) return form.bakery_name.trim() && form.owner_name.trim();
    if (step === 1) return form.slug.trim();
    return true;
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/onboarding", form);
      await refresh();
      toast.success("Your bakery is ready!");
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream paper-grain">
      <div className="mx-auto max-w-2xl px-5 py-10">
        <Logo />
        <div className="mt-10">
          <div className="mb-8 flex items-center gap-2">
            {steps.map((s, i) => (
              <div key={i} className="flex flex-1 items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${i < step ? "bg-sage text-warmwhite" : i === step ? "bg-terracotta text-warmwhite" : "bg-cocoa/10 text-cocoa/40"}`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                {i < steps.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < step ? "bg-sage" : "bg-cocoa/10"}`} />}
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-cocoa/10 bg-warmwhite p-7 shadow-[0_4px_20px_rgba(64,53,47,0.05)]">
            <h1 className="font-serif text-2xl font-semibold text-cocoa sm:text-3xl">{steps[step].title}</h1>
            <p className="mt-1 text-sm text-cocoa/60">Step {step + 1} of {steps.length}</p>

            <div className="mt-6 space-y-5">
              {step === 0 && (
                <>
                  <Field label="Bakery name" required><Input value={form.bakery_name} onChange={(e) => set("bakery_name", e.target.value)} placeholder="Honey & Hearth" className="rounded-xl bg-cream" data-testid="onb-bakery-name" /></Field>
                  <Field label="Your name" required><Input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-xl bg-cream" data-testid="onb-owner-name" /></Field>
                  <Field label="City or location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Portland, OR" className="rounded-xl bg-cream" data-testid="onb-location" /></Field>
                </>
              )}
              {step === 1 && (
                <>
                  <Field label="Short bakery description"><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Small-batch sourdough and weekend treats, baked fresh for our neighborhood." className="rounded-xl bg-cream" rows={3} data-testid="onb-description" /></Field>
                  <Field label="Public storefront link" required>
                    <div className="flex items-center rounded-xl border border-input bg-cream px-3">
                      <span className="text-sm text-cocoa/40">/bakery/</span>
                      <input value={form.slug} onChange={(e) => { setSlugEdited(true); set("slug", slugify(e.target.value)); }} className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="onb-slug" />
                    </div>
                  </Field>
                  <Field label="Accent color">
                    <div className="flex flex-wrap gap-2">
                      {ACCENT_COLORS.map((c) => (
                        <button key={c} type="button" onClick={() => set("accent_color", c)} aria-label={`Accent color ${c}`} aria-pressed={form.accent_color === c} title={c} className={`h-9 w-9 rounded-full transition-transform ${form.accent_color === c ? "ring-2 ring-cocoa ring-offset-2 ring-offset-warmwhite scale-110" : ""}`} style={{ backgroundColor: c }} data-testid={`onb-accent-${c}`} />
                      ))}
                    </div>
                  </Field>
                </>
              )}
              {step === 2 && (
                <>
                  <Field label="Time zone">
                    <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                      <SelectTrigger className="rounded-xl bg-cream" data-testid="onb-timezone"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Pickup instructions"><Textarea value={form.pickup_instructions} onChange={(e) => set("pickup_instructions", e.target.value)} placeholder="Pickup from our porch. Please text when you arrive." className="rounded-xl bg-cream" rows={2} data-testid="onb-pickup" /></Field>
                  <Field label="Payment instructions"><Textarea value={form.payment_instructions} onChange={(e) => set("payment_instructions", e.target.value)} className="rounded-xl bg-cream" rows={2} data-testid="onb-payment" /></Field>
                  <Field label="Contact email"><Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="rounded-xl bg-cream" data-testid="onb-contact" /></Field>
                </>
              )}
            </div>

            {error && <p className="mt-4 text-sm text-destructive" data-testid="onb-error">{error}</p>}

            <div className="mt-8 flex items-center justify-between">
              {step > 0 ? (
                <Button variant="ghost" onClick={() => setStep(step - 1)} className="rounded-full text-cocoa" data-testid="onb-back"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              ) : <span />}
              {step < steps.length - 1 ? (
                <Button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="onb-next">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
              ) : (
                <Button onClick={submit} disabled={loading} className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="onb-finish">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create my bakery"}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="mb-1.5 block text-sm font-medium text-cocoa">{label}{required && <span className="text-terracotta"> *</span>}</Label>
      {children}
    </div>
  );
}
